import React, { useEffect } from 'react';
import { useNavigate, useParams, Location } from 'react-router-dom';
import AccountService from '@api/account/account';
import { providers } from '@api/account/factory';
import { useNotify, useQuery } from '@hooks';
import { Login, LoginType } from '@models/account';
import Config from '@utils/config';
import CircularLoading from '@components/loading';
import styles from './callback.module.scss';
import { useGlobalContext } from '@/globalContext';
import {
    AGE_ANTISPOOFING_PATH,
    LOGIN_PATH,
    ROOT_PATH,
    VERIFY_ACCOUNT_PATH,
    FACE_MATCHING_PATH,
} from '@utils/constants/Routes';

const accountService = new AccountService(Config.apiUrl);

const allowedRedirectsURLs = [ROOT_PATH, AGE_ANTISPOOFING_PATH, FACE_MATCHING_PATH];

interface Props {
    onLogin?: () => void;
}

type Params = {
    provider: LoginType;
};

const Callback: React.FC<Props> = ({ onLogin }) => {
    const query = useQuery();
    const navigate = useNavigate();
    const notify = useNotify();
    const { provider } = useParams<Params>();

    const callback = provider ? providers[provider].callback : undefined;
    const { account } = useGlobalContext();

    if (account) {
        navigate(ROOT_PATH, { replace: true });
    }

    /**
     * Get the login token to request the external service in order to get the
     * user data. It returns undefined if the callback is not able to return the
     * information.
     */
    const getData = () => {
        try {
            return callback ? callback(query) : undefined;
        } catch {
            return undefined;
        }
    };
    const data = getData();

    const dataRef = JSON.stringify(data);
    useEffect(() => {
        // If user is already logged skip the rest of the code.
        if (account) return;
        // Take callback and check if it exists.
        if (!dataRef) {
            notify({
                message: 'Please try again, there were some invalid arguments in the url.',
                variation: 'danger',
                icon: true,
            });
            navigate(ROOT_PATH, { replace: true });
            return;
        }
        const data = JSON.parse(dataRef) as Login;

        // Run callback request to get account info.
        accountService
            .callback(data)
            .then((session) => {
                if (session.verified && !session.banned) {
                    if (onLogin) onLogin();
                    let redirect: string | Location = data.redirect || ROOT_PATH;
                    if (
                        typeof redirect === 'object' &&
                        !allowedRedirectsURLs.includes(redirect.pathname)
                    ) {
                        redirect = ROOT_PATH;
                    }

                    navigate(redirect, { replace: true });
                } else {
                    navigate(VERIFY_ACCOUNT_PATH, { replace: true, state: { ...session } });
                }
            })
            .catch(() => {
                navigate(LOGIN_PATH, { replace: true });
            });
    }, [dataRef, onLogin, navigate, account, notify]);

    return (
        <div className={styles['callback__loading-container']}>
            <CircularLoading />
        </div>
    );
};

export default Callback;
